interface ClawbertMarkProps {
  pose?: "wave" | "sold" | "reading" | "sleeping";
  className?: string;
}

/**
 * Flat 2D Clawbert for the marketing page. Decorative — every instance is
 * aria-hidden; meaning is always carried by adjacent real text.
 */
export function ClawbertMark({ pose = "wave", className }: ClawbertMarkProps) {
  const coral = "#F4795B";
  const coralDeep = "#D95B3E";
  const ink = "#23201B";

  return (
    <svg
      viewBox="0 0 200 200"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {/* Antennae */}
      <path
        d="M88 62 C82 46 70 40 58 40"
        fill="none"
        stroke={coralDeep}
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M112 62 C118 46 130 40 142 40"
        fill="none"
        stroke={coralDeep}
        strokeWidth="5"
        strokeLinecap="round"
      />

      {/* Tail fan */}
      <ellipse cx="72" cy="168" rx="16" ry="10" fill={coralDeep} transform="rotate(-24 72 168)" />
      <ellipse cx="128" cy="168" rx="16" ry="10" fill={coralDeep} transform="rotate(24 128 168)" />
      <ellipse cx="100" cy="172" rx="18" ry="11" fill={coral} />

      {/* Left arm and claw */}
      {pose === "wave" ? (
        <g>
          <path d="M74 104 C56 96 44 78 46 58" fill="none" stroke={coral} strokeWidth="11" strokeLinecap="round" />
          <g transform="rotate(-30 46 52)">
            <ellipse cx="46" cy="50" rx="17" ry="13" fill={coral} />
            <path d="M34 44 L48 50 L36 58 Z" fill="#FBF3E2" />
          </g>
        </g>
      ) : (
        <g>
          <path d="M74 110 C58 112 48 122 46 134" fill="none" stroke={coral} strokeWidth="11" strokeLinecap="round" />
          <g transform="rotate(206 45 138)">
            <ellipse cx="45" cy="138" rx="17" ry="13" fill={coral} />
            <path d="M33 132 L47 138 L35 146 Z" fill="#FBF3E2" />
          </g>
        </g>
      )}

      {/* Right arm and claw */}
      {pose === "sold" ? (
        <g>
          <path d="M126 104 C144 96 154 80 154 64" fill="none" stroke={coral} strokeWidth="11" strokeLinecap="round" />
          <ellipse cx="155" cy="58" rx="15" ry="12" fill={coral} />
          {/* Sign */}
          <rect x="130" y="6" width="56" height="38" rx="7" fill="#F6BE4F" stroke={ink} strokeWidth="3" />
          <rect x="154" y="44" width="5" height="18" fill={ink} />
          <text
            x="158"
            y="31"
            textAnchor="middle"
            fontFamily="Clash Display, sans-serif"
            fontWeight="700"
            fontSize="19"
            fill={ink}
          >
            Sold!
          </text>
        </g>
      ) : pose === "reading" ? (
        <g>
          <path d="M126 108 C142 104 150 94 152 82" fill="none" stroke={coral} strokeWidth="11" strokeLinecap="round" />
          <ellipse cx="152" cy="78" rx="15" ry="12" fill={coral} />
          {/* Envelope */}
          <g transform="rotate(-12 150 60)">
            <rect x="128" y="44" width="46" height="32" rx="4" fill="#FFFDF6" stroke={ink} strokeWidth="3" />
            <path d="M128 48 L151 66 L174 48" fill="none" stroke={ink} strokeWidth="3" strokeLinejoin="round" />
          </g>
        </g>
      ) : (
        <g>
          <path d="M126 110 C142 112 152 122 154 134" fill="none" stroke={coral} strokeWidth="11" strokeLinecap="round" />
          <g transform="rotate(-26 155 138)">
            <ellipse cx="155" cy="138" rx="17" ry="13" fill={coral} />
            <path d="M167 132 L153 138 L165 146 Z" fill="#FBF3E2" />
          </g>
        </g>
      )}

      {/* Body */}
      <ellipse cx="100" cy="120" rx="35" ry="44" fill={coral} />
      {/* Belly segments */}
      <path d="M74 132 Q100 142 126 132" fill="none" stroke={coralDeep} strokeWidth="4" strokeLinecap="round" />
      <path d="M78 146 Q100 155 122 146" fill="none" stroke={coralDeep} strokeWidth="4" strokeLinecap="round" />

      {/* Eye stalks */}
      <line x1="90" y1="84" x2="86" y2="68" stroke={coral} strokeWidth="7" strokeLinecap="round" />
      <line x1="110" y1="84" x2="114" y2="68" stroke={coral} strokeWidth="7" strokeLinecap="round" />

      {/* Eyes */}
      {pose === "sleeping" ? (
        <g>
          <path d="M79 66 Q86 72 93 66" fill="none" stroke={ink} strokeWidth="3.5" strokeLinecap="round" />
          <path d="M107 66 Q114 72 121 66" fill="none" stroke={ink} strokeWidth="3.5" strokeLinecap="round" />
          <text x="146" y="110" fontFamily="Clash Display, sans-serif" fontWeight="700" fontSize="22" fill={ink} opacity="0.55">z</text>
          <text x="158" y="94" fontFamily="Clash Display, sans-serif" fontWeight="700" fontSize="16" fill={ink} opacity="0.4">z</text>
        </g>
      ) : (
        <g>
          <circle cx="86" cy="66" r="9.5" fill="#FFFDF6" />
          <circle cx="114" cy="66" r="9.5" fill="#FFFDF6" />
          <circle cx="87.5" cy="67" r="4" fill={ink} />
          <circle cx="112.5" cy="67" r="4" fill={ink} />
        </g>
      )}

      {/* Snout + smile */}
      <path d="M93 96 Q100 102 107 96" fill="none" stroke={ink} strokeWidth="3.5" strokeLinecap="round" />
      {/* Blush */}
      <circle cx="78" cy="98" r="5" fill={coralDeep} opacity="0.5" />
      <circle cx="122" cy="98" r="5" fill={coralDeep} opacity="0.5" />

      {/* Little legs */}
      <path d="M76 150 C70 156 68 162 69 166" fill="none" stroke={coralDeep} strokeWidth="6" strokeLinecap="round" />
      <path d="M124 150 C130 156 132 162 131 166" fill="none" stroke={coralDeep} strokeWidth="6" strokeLinecap="round" />
    </svg>
  );
}
